import { GROUP_PLAY_SCHEDULE } from '../constants/groupPlay'
import {
  PLAYOFF_DRAFT_ROUNDS,
  PLAYOFF_RESULTS_START_ISO,
  PLAYOFF_ROUND_1_SERIES,
  PLAYOFF_ROUND_2_SERIES,
} from '../constants/playoffs'
import type { PlayoffExtraMatchup, PlayoffSeriesPairing } from '../constants/playoffs'
import type { LeagueTeamName } from '../constants/teamNames'

/**
 * Shared playoff series helpers for the Playoffs schedule and Bracket pages.
 */

/**
 * Group-play schedule keys used to tell a first meeting from a playoff rematch
 * between the same two players.
 */
export const GROUP_PLAY_MATCHUP_KEYS = new Set(
  GROUP_PLAY_SCHEDULE.map(matchup => getSlotMatchupKey(matchup.home, matchup.away))
)

export interface PlayoffAssignedUser {
  id: number
  displayName: string
  groupPlaySlot: string | null
}

export interface PlayoffCompetitorInput {
  id: number
  username: string
  discordUsername: string | null
  discordGlobalName: string | null
  leagueTeamName: string | null
  groupPlaySlot: string | null
}

export interface PlayoffGameInput {
  id: number
  uploadedAt?: string
  homeTeam?: string
  awayTeam?: string
  homeScore: number
  awayScore: number
  player1User?: { id: number; teamAbbreviation?: string }
  player2User?: { id: number; teamAbbreviation?: string }
  forfeit?: {
    resultByUserId: Record<number, 'win' | 'loss'>
  }
}

export interface PlayoffMatchupResult {
  gameId: number
  uploadedAt?: string
  isForfeit: boolean
  scoresByUser: Record<
    number,
    {
      score: number
      teamAbbreviation: string | null
      forfeitResult?: 'win' | 'loss'
    }
  >
}

export function getSlotMatchupKey(slotA: string, slotB: string): string {
  return [slotA, slotB].sort().join('|')
}

export function getUserMatchupKey(userA: number, userB: number): string {
  return [userA, userB].sort((a, b) => a - b).join('-')
}

export function getDraftRound(groupPlaySlot: string | null): number | null {
  const [round] = groupPlaySlot?.split('.') ?? []
  const parsedRound = Number(round)
  return Number.isInteger(parsedRound) ? parsedRound : null
}

/**
 * Maps `${leagueTeamName}|${draftRound}` to the assigned competitor.
 */
const PLAYOFF_HOME_USER_ALIASES: Record<string, string> = {
  nelly: 'nel00',
  nellyville: 'nel00',
  swamp: 'swampdaddy',
  swampdaddy: 'swampdaddy',
  disciple: 'honortogod',
  honortogod: 'honortogod',
  ag: 'ag83',
  dps: 'tecmodps',
  tecmodps: 'tecmodps',
  acslater: 'acslatertecmo',
  acslatertecmo: 'acslatertecmo',
  richiesmith: 'richiesmith',
  regulator: 'regulator088',
  regulator088: 'regulator088',
  randywags: 'randywags25',
  agentmidnight: 'cmoney2081',
  agentmidight: 'cmoney2081',
  protajay: 'protajay.',
  protojay: 'protajay.',
  beefjuice: 'realbeefjuice',
  edboi: 'edboi420',
  bigred17machine: 'bigred17machine',
  uyio: '_uyio_',
}

export interface PlayoffRoundHomeAway {
  homeTeamName: LeagueTeamName
  awayTeamName: LeagueTeamName
  homeUser: PlayoffAssignedUser
  awayUser: PlayoffAssignedUser
}

/**
 * Resolves scheduled home/away users for a playoff draft round, honoring {@link PlayoffSeriesPairing.homeUserByRound}.
 */
export function resolvePlayoffRoundHomeAway(
  pairing: PlayoffSeriesPairing,
  round: number,
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  users: PlayoffCompetitorInput[] = []
): PlayoffRoundHomeAway | null {
  const teamAUser = userByTeamAndRound.get(`${pairing.teamA}|${round}`)
  const teamBUser = userByTeamAndRound.get(`${pairing.teamB}|${round}`)
  if (!teamAUser || !teamBUser) {
    return null
  }

  const homeHint = pairing.homeUserByRound?.[round]
  const designatedUser = homeHint ? findPlayoffHomeDesignatedUser(homeHint, users) : undefined

  if (designatedUser) {
    const designatedTeam = designatedUser.leagueTeamName as LeagueTeamName | null
    if (designatedUser.id === teamAUser.id) {
      return {
        homeTeamName: pairing.teamA,
        awayTeamName: pairing.teamB,
        homeUser: teamAUser,
        awayUser: teamBUser,
      }
    }
    if (designatedUser.id === teamBUser.id) {
      return {
        homeTeamName: pairing.teamB,
        awayTeamName: pairing.teamA,
        homeUser: teamBUser,
        awayUser: teamAUser,
      }
    }
    if (designatedTeam === pairing.teamA) {
      return {
        homeTeamName: pairing.teamA,
        awayTeamName: pairing.teamB,
        homeUser: teamAUser,
        awayUser: teamBUser,
      }
    }
    if (designatedTeam === pairing.teamB) {
      return {
        homeTeamName: pairing.teamB,
        awayTeamName: pairing.teamA,
        homeUser: teamBUser,
        awayUser: teamAUser,
      }
    }
  }

  const homeTeamName =
    pairing.homeTeamByRound?.[round] ?? (round % 2 === 1 ? pairing.teamA : pairing.teamB)
  const awayTeamName: LeagueTeamName = homeTeamName === pairing.teamA ? pairing.teamB : pairing.teamA
  const homeUser = userByTeamAndRound.get(`${homeTeamName}|${round}`)
  const awayUser = userByTeamAndRound.get(`${awayTeamName}|${round}`)
  if (!homeUser || !awayUser) {
    return null
  }

  return { homeTeamName, awayTeamName, homeUser, awayUser }
}

function findPlayoffHomeDesignatedUser(
  hint: string,
  users: PlayoffCompetitorInput[]
): PlayoffCompetitorInput | undefined {
  const normalizedHint = hint.toLowerCase().replace(/[^a-z0-9]/g, '')
  const usernameTarget =
    PLAYOFF_HOME_USER_ALIASES[normalizedHint] ?? hint.toLowerCase()

  return users.find(user => {
    const username = user.username.toLowerCase()
    const discordGlobal = user.discordGlobalName?.toLowerCase().replace(/[^a-z0-9]/g, '') ?? ''
    const discordUser = user.discordUsername?.toLowerCase().replace(/[^a-z0-9]/g, '') ?? ''
    const displayName = (user.discordGlobalName ?? user.discordUsername ?? user.username)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
    return (
      username === usernameTarget ||
      discordGlobal === normalizedHint ||
      discordUser === normalizedHint ||
      displayName === normalizedHint ||
      username.includes(usernameTarget)
    )
  })
}

/** One row on the Playoffs schedule, including optional rematches. */
export interface PlayoffScheduleSlot {
  scheduleKey: string
  round: number
  displayRoundLabel: string
  playoffRound: 1 | 2 | 3
  seriesGameIndex: number
  homeTeamName: LeagueTeamName
  awayTeamName: LeagueTeamName
  homeUser: PlayoffAssignedUser
  awayUser: PlayoffAssignedUser
}

function toPlayoffAssignedUser(user: PlayoffCompetitorInput): PlayoffAssignedUser {
  return {
    id: user.id,
    displayName: user.discordGlobalName ?? user.discordUsername ?? user.username,
    groupPlaySlot: user.groupPlaySlot,
  }
}

/**
 * Builds the full ordered schedule for a playoff series (draft rounds plus {@link PlayoffExtraMatchup} rows).
 */
export function buildPlayoffSeriesScheduleGames(
  pairing: PlayoffSeriesPairing,
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  users: PlayoffCompetitorInput[] = []
): PlayoffScheduleSlot[] {
  const schedule: PlayoffScheduleSlot[] = []
  const pairSeriesIndex = new Map<string, number>()

  const appendSlot = (
    slot: Omit<PlayoffScheduleSlot, 'seriesGameIndex'>
  ): void => {
    const pairKey = getUserMatchupKey(slot.homeUser.id, slot.awayUser.id)
    const seriesGameIndex = pairSeriesIndex.get(pairKey) ?? 0
    pairSeriesIndex.set(pairKey, seriesGameIndex + 1)
    schedule.push({ ...slot, seriesGameIndex })
  }

  const appendExtra = (extra: PlayoffExtraMatchup): void => {
    const homeCompetitor = findPlayoffHomeDesignatedUser(extra.homeUsername, users)
    const awayCompetitor = findPlayoffHomeDesignatedUser(extra.awayUsername, users)
    if (!homeCompetitor?.leagueTeamName || !awayCompetitor?.leagueTeamName) {
      return
    }

    appendSlot({
      scheduleKey: `${pairing.id}-${extra.id}`,
      round: extra.insertAfterRound,
      displayRoundLabel: extra.label,
      playoffRound: pairing.playoffRound,
      homeTeamName: homeCompetitor.leagueTeamName as LeagueTeamName,
      awayTeamName: awayCompetitor.leagueTeamName as LeagueTeamName,
      homeUser: toPlayoffAssignedUser(homeCompetitor),
      awayUser: toPlayoffAssignedUser(awayCompetitor),
    })
  }

  for (const round of PLAYOFF_DRAFT_ROUNDS) {
    const resolved = resolvePlayoffRoundHomeAway(pairing, round, userByTeamAndRound, users)
    if (resolved) {
      appendSlot({
        scheduleKey: `${pairing.id}-r${round}`,
        round,
        displayRoundLabel: `Round ${round}`,
        playoffRound: pairing.playoffRound,
        homeTeamName: resolved.homeTeamName,
        awayTeamName: resolved.awayTeamName,
        homeUser: resolved.homeUser,
        awayUser: resolved.awayUser,
      })
    }

    for (const extra of pairing.extraMatchups ?? []) {
      if (extra.insertAfterRound === round) {
        appendExtra(extra)
      }
    }
  }

  return schedule
}

export function buildUserByTeamAndRound(users: PlayoffCompetitorInput[]): Map<string, PlayoffAssignedUser> {
  const assignments = new Map<string, PlayoffAssignedUser>()

  for (const user of users) {
    const round = getDraftRound(user.groupPlaySlot)
    if (!user.leagueTeamName || !round) {
      continue
    }

    assignments.set(`${user.leagueTeamName}|${round}`, {
      id: user.id,
      displayName: user.discordGlobalName ?? user.discordUsername ?? user.username,
      groupPlaySlot: user.groupPlaySlot,
    })
  }

  return assignments
}

/**
 * Collects all game results keyed by sorted user id pair (newest-first per key in array order).
 */
export function buildMatchupResultsMap(games: PlayoffGameInput[]): Map<string, PlayoffMatchupResult[]> {
  const results = new Map<string, PlayoffMatchupResult[]>()
  const sortedGames = [...games].sort((a, b) => gameSortKey(b) - gameSortKey(a))

  for (const game of sortedGames) {
    const player1Id = game.player1User?.id
    const player2Id = game.player2User?.id
    if (!player1Id || !player2Id) {
      continue
    }

    const key = getUserMatchupKey(player1Id, player2Id)
    const matchupResult: PlayoffMatchupResult = {
      gameId: game.id,
      uploadedAt: game.uploadedAt,
      isForfeit: Boolean(game.forfeit),
      scoresByUser: {
        [player1Id]: {
          score: game.homeScore,
          teamAbbreviation: game.player1User?.teamAbbreviation ?? game.homeTeam ?? null,
          forfeitResult: game.forfeit?.resultByUserId[player1Id],
        },
        [player2Id]: {
          score: game.awayScore,
          teamAbbreviation: game.player2User?.teamAbbreviation ?? game.awayTeam ?? null,
          forfeitResult: game.forfeit?.resultByUserId[player2Id],
        },
      },
    }

    results.set(key, [...(results.get(key) ?? []), matchupResult])
  }

  return results
}

function gameSortKey(game: PlayoffGameInput | undefined): number {
  if (!game) {
    return 0
  }
  const parsed = new Date(game.uploadedAt ?? 0).getTime()
  return Number.isFinite(parsed) ? parsed : game.id
}

function hadScheduledGroupPlayMeeting(
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser
): boolean {
  return Boolean(
    homeUser.groupPlaySlot &&
      awayUser.groupPlaySlot &&
      GROUP_PLAY_MATCHUP_KEYS.has(getSlotMatchupKey(homeUser.groupPlaySlot, awayUser.groupPlaySlot))
  )
}

function isPlayoffEraResult(record: PlayoffMatchupResult): boolean {
  const parsed = new Date(record.uploadedAt ?? 0).getTime()
  const cutoff = new Date(PLAYOFF_RESULTS_START_ISO).getTime()
  return Number.isFinite(parsed) && parsed >= cutoff
}

/**
 * Strict Round 1 index for bracket series wins (max six games per series).
 */
function getPlayoffRound1SeriesResultRecordIndex(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser
): number | null {
  if (matchupRecords.length === 0) {
    return null
  }

  const realRecords = matchupRecords.filter(record => !record.isForfeit)
  if (realRecords.length === 0) {
    return matchupRecords.length > 0 ? 0 : null
  }

  if (realRecords.length >= 2) {
    const newestReal = realRecords[0]
    return matchupRecords.findIndex(record => record === newestReal)
  }

  const onlyReal = realRecords[0]
  const onlyRealIndex = matchupRecords.findIndex(record => record === onlyReal)
  const olderRows = matchupRecords.slice(onlyRealIndex + 1)

  if (olderRows.some(record => record.isForfeit)) {
    return onlyRealIndex
  }

  if (!hadScheduledGroupPlayMeeting(homeUser, awayUser)) {
    return onlyRealIndex
  }

  return isPlayoffEraResult(onlyReal) ? onlyRealIndex : null
}

/**
 * Resolves which head-to-head row is the Round 1 playoff result (newest-first list).
 *
 * @remarks
 * Skips a prior group-play game only when two real (non-forfeit) results exist.
 * A lone reported playoff game is not hidden behind an older mistaken forfeit row.
 */
export function getPlayoffRound1ResultRecordIndex(
  matchupRecords: PlayoffMatchupResult[],
  _homeUser: PlayoffAssignedUser,
  _awayUser: PlayoffAssignedUser
): number | null {
  if (matchupRecords.length === 0) {
    return null
  }

  const realRecords = matchupRecords.filter(record => !record.isForfeit)
  if (realRecords.length === 0) {
    return matchupRecords.length > 0 ? 0 : null
  }

  if (realRecords.length === 1) {
    const onlyRealIndex = matchupRecords.findIndex(record => record === realRecords[0])
    const olderRows = matchupRecords.slice(onlyRealIndex + 1)
    if (olderRows.some(record => record.isForfeit)) {
      return onlyRealIndex
    }
    // One reported game and no older forfeit rows: treat as the playoff result even when
    // group play was scheduled but never played between these two.
    return onlyRealIndex
  }

  const newestRealIndex = matchupRecords.findIndex(record => !record.isForfeit)
  return newestRealIndex >= 0 ? newestRealIndex : null
}

/**
 * How many head-to-head rows belong to the scheduled group-play meeting (0 or 1).
 */
function getGroupPlayMeetingSkipCount(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser
): number {
  const hadScheduledGroupPlay =
    homeUser.groupPlaySlot && awayUser.groupPlaySlot
      ? GROUP_PLAY_MATCHUP_KEYS.has(getSlotMatchupKey(homeUser.groupPlaySlot, awayUser.groupPlaySlot))
      : false

  if (!hadScheduledGroupPlay || matchupRecords.length === 0) {
    return 0
  }

  const realRecords = matchupRecords.filter(record => !record.isForfeit)
  if (realRecords.length === 0) {
    return 1
  }

  if (realRecords.length === 1) {
    const onlyRealIndex = matchupRecords.findIndex(record => record === realRecords[0])
    const olderRows = matchupRecords.slice(onlyRealIndex + 1)
    if (olderRows.some(record => record.isForfeit)) {
      return 0
    }
    return 1
  }

  return 1
}

/**
 * Counts Round 1 playoff results already recorded between the same two players.
 */
function countRound1PlayoffGamesBetweenUsers(
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  users: PlayoffCompetitorInput[] = []
): number {
  let count = 0

  for (const pairing of PLAYOFF_ROUND_1_SERIES) {
    for (const round of PLAYOFF_DRAFT_ROUNDS) {
      const resolved = resolvePlayoffRoundHomeAway(pairing, round, userByTeamAndRound, users)
      if (!resolved) {
        continue
      }
      const { homeUser: slotHome, awayUser: slotAway } = resolved

      if (!slotHome || !slotAway) {
        continue
      }

      const isScheduledPair =
        (slotHome.id === homeUser.id && slotAway.id === awayUser.id) ||
        (slotHome.id === awayUser.id && slotAway.id === homeUser.id)

      if (!isScheduledPair) {
        continue
      }

      const key = getUserMatchupKey(homeUser.id, awayUser.id)
      const pairRecords = matchupResults.get(key) ?? []
      if (
        getPlayoffRound1SeriesResultRecordIndex(pairRecords, slotHome, slotAway) !== null
      ) {
        count += 1
      }
    }
  }

  return count
}

/**
 * Counts Round 2 playoff results already recorded between the same two players.
 */
function countRound2PlayoffGamesBetweenUsers(
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  users: PlayoffCompetitorInput[] = []
): number {
  let count = 0

  for (const pairing of PLAYOFF_ROUND_2_SERIES) {
    const scheduleGames = buildPlayoffSeriesScheduleGames(pairing, userByTeamAndRound, users)
    for (const scheduleGame of scheduleGames) {
      const { homeUser: slotHome, awayUser: slotAway, seriesGameIndex } = scheduleGame
      const isScheduledPair =
        (slotHome.id === homeUser.id && slotAway.id === awayUser.id) ||
        (slotHome.id === awayUser.id && slotAway.id === homeUser.id)

      if (!isScheduledPair) {
        continue
      }

      const key = getUserMatchupKey(homeUser.id, awayUser.id)
      const pairRecords = matchupResults.get(key) ?? []
      if (
        getPlayoffRound2ResultRecordIndex(
          pairRecords,
          slotHome,
          slotAway,
          users,
          userByTeamAndRound,
          matchupResults,
          seriesGameIndex
        ) !== null
      ) {
        count += 1
      }
    }
  }

  return count
}

/**
 * Resolves which head-to-head row is the Round 2 playoff result (newest-first list).
 *
 * @remarks
 * Semifinal slots ignore group-play and Round 1 playoff results; only new Round 2 games count.
 */
export function getPlayoffRound2ResultRecordIndex(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  _users: PlayoffCompetitorInput[],
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  seriesGameIndex = 0
): number | null {
  const priorCount =
    getGroupPlayMeetingSkipCount(matchupRecords, homeUser, awayUser) +
    countRound1PlayoffGamesBetweenUsers(
      homeUser,
      awayUser,
      userByTeamAndRound,
      matchupResults,
      _users
    )

  const targetCount = priorCount + seriesGameIndex + 1
  if (matchupRecords.length < targetCount) {
    return null
  }

  return matchupRecords.length - targetCount
}

/**
 * Resolves which head-to-head row is the Round 3 (championship) playoff result.
 *
 * @remarks
 * Championship slots ignore group play, Round 1, and Round 2 results.
 */
export function getPlayoffRound3ResultRecordIndex(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  users: PlayoffCompetitorInput[],
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  seriesGameIndex = 0
): number | null {
  const priorCount =
    getGroupPlayMeetingSkipCount(matchupRecords, homeUser, awayUser) +
    countRound1PlayoffGamesBetweenUsers(
      homeUser,
      awayUser,
      userByTeamAndRound,
      matchupResults,
      users
    ) +
    countRound2PlayoffGamesBetweenUsers(
      homeUser,
      awayUser,
      userByTeamAndRound,
      matchupResults,
      users
    )

  const targetCount = priorCount + seriesGameIndex + 1
  if (matchupRecords.length < targetCount) {
    return null
  }

  return matchupRecords.length - targetCount
}

/**
 * How many older head-to-head games to skip before reading a result for this playoff round.
 */
export function getPlayoffResultRecordIndex(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  playoffRound: 1 | 2 | 3,
  users: PlayoffCompetitorInput[],
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  seriesGameIndex = 0
): number | null {
  if (playoffRound === 1) {
    return getPlayoffRound1ResultRecordIndex(matchupRecords, homeUser, awayUser)
  }

  if (playoffRound === 2) {
    return getPlayoffRound2ResultRecordIndex(
      matchupRecords,
      homeUser,
      awayUser,
      users,
      userByTeamAndRound,
      matchupResults,
      seriesGameIndex
    )
  }

  return getPlayoffRound3ResultRecordIndex(
    matchupRecords,
    homeUser,
    awayUser,
    users,
    userByTeamAndRound,
    matchupResults,
    seriesGameIndex
  )
}

/**
 * Picks the playoff result used for bracket series win/loss totals.
 */
export function resolvePlayoffSeriesGameRecord(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  playoffRound: 1 | 2 | 3,
  users: PlayoffCompetitorInput[],
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  seriesGameIndex = 0
): PlayoffMatchupResult | undefined {
  let index: number | null = null

  if (playoffRound === 1) {
    index = getPlayoffRound1SeriesResultRecordIndex(matchupRecords, homeUser, awayUser)
  } else if (playoffRound === 2) {
    index = getPlayoffRound2ResultRecordIndex(
      matchupRecords,
      homeUser,
      awayUser,
      users,
      userByTeamAndRound,
      matchupResults,
      seriesGameIndex
    )
  } else {
    index = getPlayoffRound3ResultRecordIndex(
      matchupRecords,
      homeUser,
      awayUser,
      users,
      userByTeamAndRound,
      matchupResults,
      seriesGameIndex
    )
  }

  return index === null ? undefined : matchupRecords[index]
}

/**
 * Picks the visible result row for a scheduled playoff game on the Playoffs page.
 */
export function resolveVisibleMatchupRecordForPlayoffRound(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser,
  playoffRound: 1 | 2 | 3,
  users: PlayoffCompetitorInput[],
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  seriesGameIndex = 0
): PlayoffMatchupResult | undefined {
  const index = getPlayoffResultRecordIndex(
    matchupRecords,
    homeUser,
    awayUser,
    playoffRound,
    users,
    userByTeamAndRound,
    matchupResults,
    seriesGameIndex
  )
  return index === null ? undefined : matchupRecords[index]
}

/**
 * Picks the visible result row for Round 1 (legacy helper).
 */
export function resolveVisibleMatchupRecord(
  matchupRecords: PlayoffMatchupResult[],
  homeUser: PlayoffAssignedUser,
  awayUser: PlayoffAssignedUser
): PlayoffMatchupResult | undefined {
  const hadGroupPlay =
    homeUser.groupPlaySlot && awayUser.groupPlaySlot
      ? GROUP_PLAY_MATCHUP_KEYS.has(getSlotMatchupKey(homeUser.groupPlaySlot, awayUser.groupPlaySlot))
      : false

  if (hadGroupPlay && matchupRecords.length < 2) {
    return undefined
  }

  return matchupRecords[0]
}

export interface SeriesWinsTally {
  teamAWins: number
  teamBWins: number
  teamALosses: number
  teamBLosses: number
  ties: number
}

/**
 * Counts completed games in a playoff team series using the same schedule as the Playoffs page.
 */
export function computePlayoffSeriesWins(
  pairing: PlayoffSeriesPairing,
  userByTeamAndRound: Map<string, PlayoffAssignedUser>,
  matchupResults: Map<string, PlayoffMatchupResult[]>,
  users: PlayoffCompetitorInput[] = []
): SeriesWinsTally {
  const tally: SeriesWinsTally = {
    teamAWins: 0,
    teamBWins: 0,
    teamALosses: 0,
    teamBLosses: 0,
    ties: 0,
  }

  const scheduleGames = buildPlayoffSeriesScheduleGames(pairing, userByTeamAndRound, users)

  for (const scheduleGame of scheduleGames) {
    const { homeTeamName, awayTeamName, homeUser, awayUser, seriesGameIndex } = scheduleGame

    const key = getUserMatchupKey(homeUser.id, awayUser.id)
    const records = matchupResults.get(key) ?? []
    const record = resolvePlayoffSeriesGameRecord(
      records,
      homeUser,
      awayUser,
      pairing.playoffRound,
      users,
      userByTeamAndRound,
      matchupResults,
      seriesGameIndex
    )
    if (!record) {
      continue
    }

    const winnerTeam = getSeriesGameWinnerTeam(
      homeTeamName,
      awayTeamName,
      homeUser.id,
      awayUser.id,
      record
    )
    if (winnerTeam === 'double_loss') {
      tally.teamALosses += 1
      tally.teamBLosses += 1
    } else if (winnerTeam === 'tie') {
      tally.ties += 1
    } else if (winnerTeam === pairing.teamA) {
      tally.teamAWins += 1
      tally.teamBLosses += 1
    } else if (winnerTeam === pairing.teamB) {
      tally.teamBWins += 1
      tally.teamALosses += 1
    }
  }

  return tally
}

function getSeriesGameWinnerTeam(
  homeTeamName: LeagueTeamName,
  awayTeamName: LeagueTeamName,
  homeUserId: number,
  awayUserId: number,
  record: PlayoffMatchupResult
): LeagueTeamName | 'tie' | 'double_loss' | null {
  if (record.isForfeit) {
    const homeFr = record.scoresByUser[homeUserId]?.forfeitResult
    const awayFr = record.scoresByUser[awayUserId]?.forfeitResult
    if (homeFr === 'loss' && awayFr === 'loss') {
      return 'double_loss'
    }
    if (homeFr === 'win' && awayFr === 'loss') {
      return homeTeamName
    }
    if (awayFr === 'win' && homeFr === 'loss') {
      return awayTeamName
    }
    return 'tie'
  }

  const homeScore = record.scoresByUser[homeUserId]?.score ?? 0
  const awayScore = record.scoresByUser[awayUserId]?.score ?? 0
  if (homeScore === awayScore) {
    return 'tie'
  }

  const homeWon = homeScore > awayScore
  return homeWon ? homeTeamName : awayTeamName
}

/**
 * Bracket slot detail strings: only the leading team shows `W-L` (wins-losses, max six games).
 */
export function getBracketLeaderSlotDetails(tally: SeriesWinsTally): [string, string] {
  const { teamAWins, teamBWins, teamALosses, teamBLosses } = tally
  const gamesPlayed = teamAWins + teamBWins + teamALosses + teamBLosses + tally.ties

  if (gamesPlayed === 0) {
    return ['', '']
  }

  if (teamAWins === teamBWins) {
    if (teamAWins === 0) {
      if (teamALosses > 0 || teamBLosses > 0) {
        return [`0-${teamBLosses}`, `0-${teamALosses}`]
      }
      return ['', '']
    }
    return [`${teamAWins}-${teamBWins}`, `${teamBWins}-${teamAWins}`]
  }

  if (teamAWins > teamBWins) {
    return [`${teamAWins}-${teamBWins}`, '']
  }

  return ['', `${teamBWins}-${teamAWins}`]
}

/**
 * Returns the declared series winner display name for the champion column, if known.
 */
export function getPlayoffChampionDisplayName(
  pairing: { teamA: LeagueTeamName; teamB: LeagueTeamName },
  tally: SeriesWinsTally,
  championTeam: LeagueTeamName | null | undefined,
  getDisplayName: (team: LeagueTeamName) => string
): string | null {
  const winnerTeam =
    championTeam ??
    (tally.teamAWins > tally.teamBWins
      ? pairing.teamA
      : tally.teamBWins > tally.teamAWins
        ? pairing.teamB
        : null)

  return winnerTeam ? getDisplayName(winnerTeam) : null
}

/**
 * @deprecated Prefer {@link getBracketLeaderSlotDetails} for bracket UI.
 */
export function formatSeriesRecordDetail(tally: SeriesWinsTally): string {
  return `Series: ${tally.teamAWins}-${tally.teamBWins}${tally.ties > 0 ? `-${tally.ties}` : ''}`
}
